import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import { getWordOfTheDay } from '../../data/jewishWords';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScroll, faBook, faChevronRight, faWandMagicSparkles, faCalendar, faUsers, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { getExperienceIcon } from '../experiences/ExperienceTimelineCard';
import { calculateProgress } from '../../utils/experiences';
import { getCurrentWeeklyReading, getUpcomingHolidays } from '../../utils/hebrewCalendar';
import { mapHolidayToExperienceType } from '../../utils/feedAlgorithm';
import ParashaDetailModal from './ParashaDetailModal';
import WordDetailModal from './WordDetailModal';
import { PRIMARY_COLOR } from '../../constants/colors';

function calculateDaysUntil(experience) {
  if (!experience?.date) return null;
  const date = experience.date?.toDate ? experience.date.toDate() : (experience.date ? new Date(experience.date) : null);
  if (!date) return null;
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : null;
}

export default function ThisWeekMobileCompact() {
  const navigate = useNavigate();
  const { familyId } = useFamily();
  const [activeExperience, setActiveExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showParashaModal, setShowParashaModal] = useState(false);
  const [showWordModal, setShowWordModal] = useState(false);
  const [hasExperienceForHoliday, setHasExperienceForHoliday] = useState(false);
  
  const wordOfTheDay = getWordOfTheDay();
  const weeklyReading = getCurrentWeeklyReading();
  const upcomingHolidays = getUpcomingHolidays(10);
  // Get next major holiday (skip Rosh Chodesh)
  const nextHoliday = upcomingHolidays.find(h => !h.isFullMoon && h.isMajorHoliday) || upcomingHolidays.find(h => !h.isFullMoon) || upcomingHolidays[0];

  // Load active experience
  useEffect(() => {
    if (!familyId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experiences = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Find the next upcoming experience
      const now = new Date();
      const upcoming = experiences
        .filter(exp => {
          const expDate = exp.date?.toDate ? exp.date.toDate() : (exp.date ? new Date(exp.date) : null);
          return expDate && expDate > now;
        })
        .sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateA - dateB;
        });
      
      if (upcoming.length > 0) {
        setActiveExperience(upcoming[0]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading experience:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [familyId]);

  // Check if user already has an experience for the next holiday
  useEffect(() => {
    if (!familyId || !nextHoliday) {
      setHasExperienceForHoliday(false);
      return;
    }

    const experienceType = mapHolidayToExperienceType(nextHoliday.name);
    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId),
      where('type', '==', experienceType)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setHasExperienceForHoliday(snapshot.docs.length > 0);
    }, (error) => {
      console.error('Error checking experiences for holiday:', error);
      setHasExperienceForHoliday(false);
    });

    return unsubscribe;
  }, [familyId, nextHoliday]);

  const handleHolidayClick = () => {
    if (!nextHoliday) return;
    
    if (hasExperienceForHoliday) {
      // Navigate to experiences page
      navigate('/experiences');
    } else {
      // Create new experience for this holiday
      const experienceType = mapHolidayToExperienceType(nextHoliday.name);
      window.dispatchEvent(new CustomEvent('createExperience', { 
        detail: { 
          type: experienceType, 
          holidayName: nextHoliday.name,
          date: nextHoliday.date
        } 
      }));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  return (
    <>
      {/* Desktop: Keep existing horizontal layout */}
      <div className="hidden md:block px-8 py-8">
        {/* This will be handled by existing ThisWeekHorizontal component */}
      </div>

      {/* Mobile: Compact 2-row layout */}
      <div className="md:hidden px-4 py-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3"
        >
          {/* Row 1: Shabbat + Word (side by side) */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3">
            {/* SHABBAT CARD - Enhanced with Parasha */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowParashaModal(true)}
              className="bg-white rounded-2xl border border-border p-4 active:bg-gray-50 transition-colors flex flex-col items-center text-center relative group touch-target"
            >
              <FontAwesomeIcon icon={faScroll} className="text-primary text-2xl mb-2" />
              
              <h3 className="text-xs font-semibold text-primary mb-2">
                This Shabbat
              </h3>

              {weeklyReading ? (
                <>
                  {/* Line 2: Hebrew name with English in brackets */}
                  <div className="text-sm font-semibold text-text-primary mb-0.5">
                    {weeklyReading.hebrewName} ({weeklyReading.englishName})
                  </div>
                  {/* Line 3: Main idea */}
                  <div className="text-xs text-text-secondary mb-0.5">
                    {weeklyReading.mainIdea}
                  </div>
                  {/* Line 4: Haftarah */}
                  <div className="text-xs text-text-secondary italic">
                    {weeklyReading.haftarah.source.split(' ')[0]} • {weeklyReading.haftarah.mainIdea}
                  </div>
                </>
              ) : (
                <div className="text-sm font-semibold text-text-primary">
                  Special Shabbat
                </div>
              )}
            </motion.button>

            {/* WORD CARD - Compact with Hebrew and English same size */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowWordModal(true)}
              className="bg-white rounded-2xl border border-border p-4 active:bg-gray-50 transition-colors flex flex-col items-center text-center touch-target"
            >
              <FontAwesomeIcon icon={faBook} className="text-primary text-2xl mb-2" />
              
              <h3 className="text-xs font-semibold text-primary mb-2">
                Word of the Day
              </h3>

              {/* Line 2: Hebrew word and transliteration */}
              <div className="text-sm font-semibold text-text-primary mb-0.5">
                <span className="font-serif" dir="rtl">
                  {wordOfTheDay.hebrew}
                </span>
                {' '}
                <span>({wordOfTheDay.transliteration})</span>
              </div>
              {/* Line 3: Meaning */}
              <div className="text-xs text-text-secondary mb-0.5">
                {wordOfTheDay.meaning}
              </div>
              {/* Line 4: Language */}
              {wordOfTheDay.language && (
                <div className="text-xs text-text-secondary italic">
                  in {wordOfTheDay.language}
                </div>
              )}
            </motion.button>
          </motion.div>

          {/* Row 2: Next Holiday (full width) */}
          {nextHoliday ? (
            <motion.button
              variants={itemVariants}
              whileTap={{ scale: 0.95 }}
              onClick={handleHolidayClick}
              className="bg-white rounded-2xl border border-border p-4 active:bg-gray-50 transition-colors flex flex-col items-center text-center min-h-[100px] touch-target w-full"
            >
              <FontAwesomeIcon icon={faCalendar} className="text-primary text-2xl mb-2" />
              
              {/* Line 1: Coming Up */}
              <h3 className="text-xs font-semibold text-primary mb-2">
                Coming Up
              </h3>
              
              {/* Line 2: Holiday name */}
              <div className="text-sm font-semibold text-text-primary mb-0.5">
                {nextHoliday.name}
              </div>
              {/* Line 3: Description */}
              <div className="text-xs text-text-secondary mb-0.5">
                {nextHoliday.isMajorHoliday ? 'Major Jewish holiday' : 'Upcoming celebration'}
              </div>
              {/* Line 4: Date and days away */}
              <div className="text-xs text-text-secondary italic">
                {nextHoliday.date ? nextHoliday.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} • {nextHoliday.daysUntil} days away
              </div>
            </motion.button>
          ) : (
            <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-border p-4 text-center min-h-[100px] w-full">
              <div className="text-xs text-text-secondary">No upcoming holidays</div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      <ParashaDetailModal
        isOpen={showParashaModal}
        onClose={() => setShowParashaModal(false)}
        parasha={weeklyReading}
      />

      <WordDetailModal
        isOpen={showWordModal}
        onClose={() => setShowWordModal(false)}
        word={wordOfTheDay}
      />
    </>
  );
}


